// The in-app link contract of the AI assistant (docs/ai-module-design-2026-10-30.md §8.4).
// The system prompt tells the model to write exactly these links; the chat
// renders them as router navigations. Anything else is NOT a link: model text
// is untrusted (it echoes database content), so only whitelisted shapes with
// numeric ids ever become clickable.
//
//   aiLinkRoute('/order-history?order=12') -> { path: '/order-history', state: { selectedOrderId: 12 } }
//   explorerLink('https://tronscan.org/#/transaction/abc') -> that URL (new tab) | null

const ID_RE = /^\d+$/;

// Plain pages the bot may link to (App.js route guards still decide access).
const PLAIN_PATHS = new Set([
  '/dashboard', '/create-order', '/order-history', '/task-tracking', '/transaction-history',
  '/money', '/crypto', '/blockchain', '/platform', '/expenses', '/checkpoints', '/reports',
  '/customers', '/users', '/ai-assistant',
]);

const TX_SOURCES = new Set(['money', 'crypto', 'blockchain']);

// path -> [query key, router-state key]
const ID_LINKS = {
  '/order-history': ['order', 'selectedOrderId'],
  '/platform': ['platform_order', 'selectedPlatformOrderId'],
  '/customers': ['customer', 'focusCustomerId'],
  '/expenses': ['expense', 'focusExpenseId'],
  '/task-tracking': ['task', 'focusTaskId'],
};

export function aiLinkRoute(href) {
  if (typeof href !== 'string') return null;
  const raw = href.trim();
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.length > 200) return null;
  const hashAt = raw.indexOf('#');
  const noHash = hashAt >= 0 ? raw.slice(0, hashAt) : raw;
  const qAt = noHash.indexOf('?');
  let path = qAt >= 0 ? noHash.slice(0, qAt) : noHash;
  const query = qAt >= 0 ? noHash.slice(qAt + 1) : '';
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  if (!PLAIN_PATHS.has(path)) return null;

  let params;
  try { params = new URLSearchParams(query); } catch (e) { return null; }

  if (ID_LINKS[path]) {
    const [qKey, stateKey] = ID_LINKS[path];
    if (params.has(qKey)) {
      const id = params.get(qKey);
      if (!ID_RE.test(id)) return null;
      return { path, state: { [stateKey]: Number(id) } };
    }
  }
  if (path === '/transaction-history' && (params.has('id') || params.has('source'))) {
    const id = params.get('id');
    const source = params.get('source');
    if (!ID_RE.test(id || '') || !TX_SOURCES.has(source)) return null;
    return { path, state: { focusTransaction: { id: Number(id), source } } };
  }
  if (path === '/checkpoints' && params.has('checkpoint') && !ID_RE.test(params.get('checkpoint'))) return null;
  return { path, state: undefined };
}

const EXPLORER_HOSTS = new Set([
  'tronscan.org', 'www.tronscan.org',
  'etherscan.io', 'www.etherscan.io',
  'bscscan.com', 'www.bscscan.com',
]);

// https:// block-explorer links only; returns the normalised URL or null.
export function explorerLink(href) {
  if (typeof href !== 'string' || href.length > 500) return null;
  try {
    const u = new URL(href.trim());
    if (u.protocol !== 'https:' || u.username || u.password) return null;
    if (!EXPLORER_HOSTS.has(u.hostname.toLowerCase())) return null;
    return u.toString();
  } catch (e) {
    return null;
  }
}
